/**
 * Release 3.3 item C2: "is exposure going down?" on the Discovery dashboard.
 *
 * Unresolved findings (area, left axis), findings resolved that day (bars) and
 * the risk score (line, right axis: the sum of the data-risk score of the assets
 * that have an open finding) over 30, 90 or 365 days,
 * from GET /v1/discovery/risk-series: the daily snapshot, per IST day, for the
 * business units the signed-in user can see. No demo data and no constant: a
 * tenant with no history gets an empty state, history shorter than the range
 * says when it starts, and a day with no snapshot is a gap in the line.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Area, Bar, Line, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend,
} from 'recharts';
import { Loader2, TrendingDown } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getRiskSeries, type RiskSeries, type RiskSeriesRange } from '@/lib/api';

import { describeSeries } from './risk-series-view';

const RANGES: ReadonlyArray<{ days: RiskSeriesRange; label: string }> = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
];

type Load = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; series: RiskSeries };

export function RiskReductionCard(): JSX.Element {
  const [range, setRange] = useState<RiskSeriesRange>(30);
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    let live = true;
    setLoad({ status: 'loading' });
    getRiskSeries(range)
      .then((series) => live && setLoad({ status: 'ready', series }))
      .catch((e: unknown) => live && setLoad({ status: 'error', message: e instanceof Error ? e.message : 'request failed' }));
    return () => {
      live = false;
    };
  }, [range]);

  const view = useMemo(() => (load.status === 'ready' ? describeSeries(load.series) : null), [load]);

  return (
    <GlassCard className="p-6" data-testid="risk-reduction-card">
      <CardHeader className="p-0 mb-4 flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-primary" /> Risk reduction over time
          </CardTitle>
          <CardDescription clamp>
            Unresolved findings, findings resolved each day and the risk score, per IST day
          </CardDescription>
        </div>
        <div className="flex gap-1" role="group" aria-label="Range">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={range === r.days ? 'default' : 'ghost'}
              aria-pressed={range === r.days}
              data-testid={`risk-series-range-${r.days}`}
              onClick={() => setRange(r.days)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </CardHeader>

      {load.status === 'loading' && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-10 justify-center" data-testid="risk-series-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading history
        </div>
      )}

      {load.status === 'error' && (
        <div className="text-xs text-muted-foreground py-10 text-center" role="alert" data-testid="risk-series-error">
          The history could not be loaded ({load.message}). Nothing is shown rather than a guess.
        </div>
      )}

      {view?.state === 'empty' && (
        <div className="text-xs text-muted-foreground py-10 text-center" data-testid="risk-series-empty">
          {view.message}
        </div>
      )}

      {view?.state === 'ready' && load.status === 'ready' && (
        <>
          {view.headline && (
            <p className="text-sm mb-3" data-testid="risk-series-headline">
              {view.headline}
            </p>
          )}
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={view.rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" minTickGap={24} />
              <YAxis yAxisId="count" allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
              <YAxis yAxisId="score" orientation="right" allowDecimals={false} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
              <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="count" type="monotone" dataKey="open" name="Unresolved findings" connectNulls={false} stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} strokeWidth={2} />
              <Bar yAxisId="count" dataKey="resolved" name="Resolved that day" fill="hsl(var(--success))" fillOpacity={0.7} />
              <Line yAxisId="score" type="monotone" dataKey="riskScoreTotal" name="Risk score" connectNulls={false} stroke="hsl(var(--sev-critical))" strokeWidth={2} dot={view.rows.length < 40} />
            </ComposedChart>
          </ResponsiveContainer>
          {view.historyNote && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="risk-series-history-note">
              {view.historyNote}
            </p>
          )}
          {view.gapDays > 0 && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="risk-series-gaps">
              {view.gapDays} day{view.gapDays === 1 ? '' : 's'} in this window had no snapshot and appear as gaps.
            </p>
          )}
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="risk-series-definition">
            {load.series.openDefinition} {load.series.riskFormula}
          </p>
        </>
      )}
    </GlassCard>
  );
}
